import React, { useState, useEffect } from 'react';
import { websiteAnalyticsService } from '../../services/websiteAnalyticsService';
import { UserBehaviorMetrics, WebsiteAnalyticsFilters } from '../../types/websiteAnalytics';
import MetricCard from '../Common/MetricCard';
import BarChart from '../Charts/BarChart';
import PieChart from '../Charts/PieChart';

interface UserBehaviorDashboardProps {
  filters?: WebsiteAnalyticsFilters;
  refreshInterval?: number;
}

export const UserBehaviorDashboard: React.FC<UserBehaviorDashboardProps> = ({
  filters,
  refreshInterval = 300000 // 5 minutes
}) => {
  const [metrics, setMetrics] = useState<UserBehaviorMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserBehaviorMetrics = async () => {
    try {
      setLoading(true);
      const response = await websiteAnalyticsService.getUserBehaviorMetrics(filters);
      
      if (response.success && response.data) {
        setMetrics(response.data);
        setError(null);
      } else {
        setError('Failed to fetch user behavior metrics');
      }
    } catch (err) {
      setError('Error loading user behavior data');
      console.error('Error fetching user behavior metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserBehaviorMetrics();
    
    const interval = setInterval(fetchUserBehaviorMetrics, refreshInterval);
    return () => clearInterval(interval);
  }, [filters, refreshInterval]);

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchUserBehaviorMetrics}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No user behavior data available</p>
      </div>
    );
  }

  const formatDuration = (seconds: number): string => {
    return websiteAnalyticsService.formatDuration(seconds);
  };

  const formatPercentage = (value: number): string => {
    return websiteAnalyticsService.formatPercentage(value);
  };

  const formatNumber = (value: number): string => {
    return websiteAnalyticsService.formatNumber(value);
  };

  // Prepare chart data for Recharts - with safety checks
  const topPagesChartData = (metrics.topPages || []).slice(0, 10).map(page => ({
    name: page.title || page.page,
    views: page.views
  }));

  const deviceBreakdownData = (metrics.deviceBreakdown || []).map(device => ({
    name: device.deviceType ? device.deviceType.charAt(0).toUpperCase() + device.deviceType.slice(1) : 'Unknown',
    value: device.percentage * 100,
    sessions: device.sessions
  }));

  const browserBreakdownData = (metrics.browserBreakdown || []).map(browser => ({
    name: browser.browser || 'Unknown',
    value: (browser.percentage || 0) * 100,
    sessions: browser.sessions
  }));

  const renderOverviewContent = () => (
    <>
      {/* Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Sessions"
          value={formatNumber(metrics.totalSessions || 0)}
          icon={<span>👥</span>}
          color="blue"
        />
        <MetricCard
          title="Page Views"
          value={formatNumber(metrics.totalPageViews || 0)}
          icon={<span>👁️</span>}
          color="green"
        />
        <MetricCard
          title="Avg Session Duration"
          value={formatDuration(metrics.averageSessionDuration || 0)}
          icon={<span>⏱️</span>}
          color="purple"
        />
        <MetricCard
          title="Bounce Rate"
          value={formatPercentage(metrics.bounceRate || 0)}
          icon={<span>📉</span>}
          color={(metrics.bounceRate || 0) < 0.5 ? 'green' : 'red'}
        />
      </div>

      {/* User Engagement Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard
          title="Pages per Session"
          value={(metrics.pagesPerSession || 0).toFixed(1)}
          icon={<span>📄</span>}
          color={(metrics.pagesPerSession || 0) > 2 ? 'green' : 'yellow'}
        />
        <MetricCard
          title="New Users"
          value={formatNumber(metrics.newUsers || 0)}
          icon={<span>🆕</span>}
          color="blue"
        />
        <MetricCard
          title="Returning Users"
          value={formatNumber(metrics.returningUsers || 0)}
          icon={<span>🔄</span>}
          color="purple"
        />
      </div>

      {/* Top Pages Chart */}
      <BarChart
        data={topPagesChartData}
        xAxisKey="name"
        bars={[{
          key: 'views',
          color: '#3B82F6',
          name: 'Page Views'
        }]}
        title="Most Visited Pages"
        height={320}
      />

      {/* Device and Browser Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PieChart
          data={deviceBreakdownData}
          title="Device Breakdown"
          height={280}
          colors={['#3B82F6', '#10B981', '#F59E0B', '#EF4444']}
        />

        <PieChart
          data={browserBreakdownData}
          title="Browser Breakdown"
          height={280}
          colors={['#6366F1', '#EC4899', '#0EA5E9', '#22C55E', '#FB923C']}
        />
      </div>

      {/* Top Pages Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Page Performance Details</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Page
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Views
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unique Views
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bounce Rate
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(metrics.topPages || []).slice(0, 10).map((page, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {page.title || page.page}
                      </div>
                      <div className="text-sm text-gray-500">{page.page}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatNumber(page.views)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatNumber(page.uniqueViews)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDuration(page.averageDuration)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatPercentage(page.bounceRate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Geographic Distribution */}
      {(metrics.geographicData || []).length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Geographic Distribution</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sessions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Percentage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Duration
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(metrics.geographicData || []).slice(0, 10).map((location, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {location.city ? `${location.city}, ${location.country}` : location.country}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(location.sessions)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPercentage(location.percentage)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDuration(location.averageDuration)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      {/* User Behavior Overview - No extra tabs needed since main page handles navigation */}
      {renderOverviewContent()}
    </div>
  );
};